// Standalone availability grid for the profile page.
// Uses event delegation on the grid container for click handling
// since DOMQL lifecycle hooks don't fire on deeply nested childExtends cells.

import { findMe, currentUser, userIsAdmin, canAdmin } from '../functions/index.js'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const _hhmmToMin = (s) => {
  if (!s) return 0
  const [h, m] = s.split(':').map(Number)
  return h * 60 + m
}

const _isAvailableDefault = (user, hour) => {
  const wh = user?.workingHours
  if (!wh) return true
  const minute = hour * 60
  const start = _hhmmToMin(wh.start)
  const end = _hhmmToMin(wh.end)
  return start < end
    ? (minute >= start && minute < end)
    : (minute >= start || minute < end)
}

const _isAvailableAt = (user, day, hour) => {
  if (!user) return false
  const grid = user.availabilityGrid
  if (grid && grid[day] && typeof grid[day][hour] === 'boolean') {
    return grid[day][hour]
  }
  return _isAvailableDefault(user, hour)
}

const _me = findMe

// CU-10 §90: roles/isAdmin removed from person object. Use canAdmin() which
// unions JWT perm check (admin.pages.read) with legacy role fallback.
const _currentUserIsAdmin = (root) => {
  if (!root) return false
  // canAdmin uses Phase E JWT claim as primary path; falls back to userIsAdmin
  // which reads root.roles.byUser (populated from server member-roles endpoint).
  return canAdmin({ root })
}

export const ProfileAvailabilityCell = {
  tag: 'button',
  border: 'none',
  padding: '0',
  height: 'A',
  minHeight: 'A',
  background: (el, s) => {
    const u = _me(el.state)
    return _isAvailableAt(u, s.day, s.h) ? 'green' : 'red.15'
  },
  cursor: (el) => _currentUserIsAdmin(el.state?.root) ? 'pointer' : 'not-allowed',
  ':hover': { opacity: '.85' }
}

export const ProfileAvailabilityGrid = {
  display: 'grid',
  gridTemplateColumns: 'auto repeat(24, 1fr)',
  gridTemplateRows: 'auto repeat(7, auto)',
  gap: '1px',
  background: 'line',
  padding: '1px',
  round: 'Z',
  overflow: 'hidden',
  userSelect: 'none',

  // Event delegation: single click handler on the grid container
  onCreate: (el, s) => {
    if (!el.node) return
    el.node.addEventListener('click', (e) => {
      const btn = e.target.closest('button')
      if (!btn) return
      const root = s.root || el.state?.root
      if (!_currentUserIsAdmin(root)) return

      // Find day/hour from the button's data attributes
      const day = parseInt(btn.dataset.day)
      const hour = parseInt(btn.dataset.hour)
      if (isNaN(day) || isNaN(hour)) return

      const user = _me(el.state)
      if (!user) return

      const grid = user.availabilityGrid
        ? user.availabilityGrid.map(row => row.slice())
        : Array.from({ length: 7 }, () => Array.from({ length: 24 }, (_, h) => _isAvailableDefault(user, h)))
      grid[day][hour] = !grid[day][hour]

      const next = (root.people || []).map(p =>
        p.email === user.email ? { ...p, availabilityGrid: grid } : p
      )
      root.update({ people: next })

      // Immediate visual feedback
      btn.style.background = grid[day][hour]
        ? 'var(--color-green)'
        : 'rgba(235, 102, 80, 0.15)'
    })
  },

  Corner: { background: 'modal', padding: 'Y' },

  HourHeaders: {
    display: 'contents',
    childExtends: {
      tag: 'span',
      background: 'modal',
      fontSize: 'Y2',
      color: 'caption',
      fontFamily: 'monospace',
      textAlign: 'center',
      padding: 'Y 0',
      text: (el, s) => String(s.h).padStart(2, '0')
    },
    childrenAs: 'state',
    children: () => Array.from({ length: 24 }, (_, h) => ({ h }))
  },

  DayRows: {
    display: 'contents',
    childExtends: {
      display: 'contents',
      Label: {
        tag: 'span',
        background: 'modal',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '600',
        padding: 'Y Z',
        align: 'center right',
        text: (el, s) => s.label
      },
      Cells: {
        display: 'contents',
        childExtends: {
          tag: 'button',
          border: 'none',
          padding: '0',
          height: 'A',
          minHeight: 'A',
          background: (el, s) => {
            const u = _me(el.state)
            return _isAvailableAt(u, s.day, s.h) ? 'green' : 'red.15'
          },
          cursor: (el) => _currentUserIsAdmin(el.state?.root) ? 'pointer' : 'not-allowed',
          ':hover': { opacity: '.85' },
          // Store day/hour as data attributes for the delegated click handler
          attr: {
            'data-day': (el, s) => s.day,
            'data-hour': (el, s) => s.h
          }
        },
        childrenAs: 'state',
        children: (el, s) => Array.from({ length: 24 }, (_, h) => ({ day: s.day, h }))
      }
    },
    childrenAs: 'state',
    children: () => DAYS.map((label, day) => ({ day, label }))
  }
}
